import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stage, Layer, Image, Rect, Group } from 'react-konva';
import Konva from 'konva';
import useImage from 'use-image';
import { API_URL } from './Config';
import arrowSvg from './assets/arrow.svg';

function VideoCanvasPage() {
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [timelineItem, setTimelineItem] = useState(null);
  const [videoElement, setVideoElement] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // Canvas dimensions
  const CANVAS_WIDTH = 1920;
  const CANVAS_HEIGHT = 1080;

  // Fetch timeline item and project data
  useEffect(() => {
    const itemId = searchParams.get('itemId');
    const projectId = window.location.pathname.split('/')[2]; // Extract from /project/10/canvas
    console.log('ItemId from URL:', itemId);
    console.log('ProjectId from URL:', projectId);
    
    if (itemId && projectId) {
      // Fetch both timeline item and project data
      Promise.all([
        fetch(`${API_URL}/timeline-items/${itemId}`).then(r => r.json()),
        fetch(`${API_URL}/projects/${projectId}`).then(r => r.json())
      ])
        .then(([timelineData, projectData]) => {
          console.log('Timeline item loaded:', timelineData);
          console.log('Project data loaded:', projectData);
          console.log('Project video URL:', projectData.videoUrl);
          setTimelineItem({ ...timelineData, projectVideoUrl: projectData.videoUrl });
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }
  }, [searchParams]);

  // Video setup and loading
  useEffect(() => {
    if (!timelineItem) return;

    // Create video element
    const video = document.createElement('video');
    video.width = 1280;
    video.height = 720;
    video.muted = true;
    video.loop = true;
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    
    // Add video to DOM (hidden) to ensure proper loading
    video.style.position = 'absolute';
    video.style.left = '-9999px';
    video.style.visibility = 'hidden';
    document.body.appendChild(video);
    
    videoRef.current = video;

    // Try multiple video sources/formats - use timeline item video if available
    const videoSources = [];
    
    if (timelineItem?.video_url) {
      const videoUrl = `${API_URL}${timelineItem.video_url}`;
      console.log('Using timeline item video URL:', videoUrl);
      videoSources.push(
        { src: videoUrl, type: 'video/quicktime' },
        { src: videoUrl, type: 'video/mp4' }
      );
    } else {
      console.log('No timeline item video URL, timelineItem:', timelineItem);
    }
    
    // Always add fallback
    videoSources.push({ src: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4', type: 'video/mp4' });
    
    console.log('All video sources:', videoSources);
    
    videoSources.forEach((source, index) => {
      const videoSource = document.createElement('source');
      videoSource.src = source.src;
      videoSource.type = source.type;
      video.appendChild(videoSource);
      console.log(`Video source ${index + 1}:`, source.src, source.type);
    });

    // Handle video events
    const handleVideoReady = () => {
      console.log('Video ready - readyState:', video.readyState, 'videoWidth:', video.videoWidth, 'videoHeight:', video.videoHeight);
      
      // Wait for video dimensions to be available
      if (!video.videoWidth || !video.videoHeight) {
        console.log('Video dimensions not ready, waiting...');
        setTimeout(handleVideoReady, 100);
        return;
      }

      setVideoElement(video);
      setIsVideoReady(true);
      
      // Play video
      video.play().catch(error => {
        console.warn('Video play failed:', error);
        // Retry after a delay
        setTimeout(() => {
          if (video.paused) {
            video.play().catch(e => console.warn('Retry failed:', e));
          }
        }, 500);
      });
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleVideoReady);
    video.addEventListener('loadeddata', () => {
      console.log('Video data loaded');
      if (!isVideoReady) {
        setTimeout(handleVideoReady, 50);
      }
    });
    video.addEventListener('canplay', () => {
      console.log('Video can play');
      if (!isVideoReady) {
        setTimeout(handleVideoReady, 50);
      }
    });
    video.addEventListener('loadstart', () => console.log('Video load started'));
    video.addEventListener('error', (e) => {
      console.error('Video error event:', e);
      if (video.error) {
        console.error('Video error code:', video.error.code);
        console.error('Video error message:', video.error.message);
        const errorMessages = {
          1: 'MEDIA_ERR_ABORTED - The user aborted the video',
          2: 'MEDIA_ERR_NETWORK - A network error occurred',
          3: 'MEDIA_ERR_DECODE - Error decoding the video',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Video format not supported'
        };
        console.error('Error meaning:', errorMessages[video.error.code] || 'Unknown error');
      }
    });

    // Start loading video
    video.load();

    // Cleanup
    return () => {
      if (video) {
        // Remove event listeners
        video.removeEventListener('loadedmetadata', handleVideoReady);
        
        // Pause video safely
        if (!video.paused) {
          video.pause();
        }
        
        // Clear video source
        video.src = '';
        
        // Remove video from DOM
        if (video.parentNode) {
          video.parentNode.removeChild(video);
        }
      }
      
      setVideoElement(null);
      setIsVideoReady(false);
    };
  }, [timelineItem]);

  // Scale canvas display to 60% of screen width
  const getCanvasScale = () => {
    const screenWidth = window.innerWidth;
    const displayWidth = screenWidth * 0.6;
    return displayWidth / CANVAS_WIDTH;
  };

  const scale = getCanvasScale();
  const displayWidth = CANVAS_WIDTH * scale;
  const displayHeight = CANVAS_HEIGHT * scale;

  // Function to fit video within canvas maintaining aspect ratio
  const getVideoProps = () => {
    if (!videoElement || !isVideoReady) return { x: 0, y: 0, width: 0, height: 0 };

    const videoWidth = videoElement.videoWidth || videoElement.width;
    const videoHeight = videoElement.videoHeight || videoElement.height;
    
    // Calculate scale to fit video within canvas
    const scaleX = CANVAS_WIDTH / videoWidth;
    const scaleY = CANVAS_HEIGHT / videoHeight;
    const videoScale = Math.min(scaleX, scaleY); // Use smaller scale to ensure video fits
    
    const scaledWidth = videoWidth * videoScale;
    const scaledHeight = videoHeight * videoScale;
    
    return {
      x: (CANVAS_WIDTH - scaledWidth) / 2,
      y: (CANVAS_HEIGHT - scaledHeight) / 2,
      width: scaledWidth,
      height: scaledHeight
    };
  };

  const videoProps = getVideoProps();

  // Animation frame for video updates
  useEffect(() => {
    if (!videoElement || !isVideoReady) return;

    let animationId;
    const updateVideo = () => {
      // Force re-render of the image to update video frame
      if (imageRef.current) {
        imageRef.current.getLayer().batchDraw();
      }
      animationId = requestAnimationFrame(updateVideo);
    };

    animationId = requestAnimationFrame(updateVideo);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [videoElement, isVideoReady]);

  // Arrow component
  const TestArrow = () => {
    const [arrowImage] = useImage(arrowSvg);
    const [arrowProps, setArrowProps] = useState({
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT / 2,
      width: 200,
      height: 80,
      rotation: 0
    });

    const handleArrowTransform = () => {
      if (imageRef.current) {
        const node = imageRef.current;
        setArrowProps({
          x: node.x(),
          y: node.y(),
          width: node.width() * node.scaleX(),
          height: node.height() * node.scaleY(),
          rotation: node.rotation()
        });
      }
    };

    // Calculate bounding box dimensions based on arrow rotation
    const angleRad = (arrowProps.rotation || 0) * Math.PI / 180;
    const cos = Math.abs(Math.cos(angleRad));
    const sin = Math.abs(Math.sin(angleRad));
    const boundingWidth = arrowProps.width * cos + arrowProps.height * sin;
    const boundingHeight = arrowProps.width * sin + arrowProps.height * cos;

    return (
      <Group>
        {/* Dynamic bounding rectangle */}
        <Rect
          x={arrowProps.x - boundingWidth/2}
          y={arrowProps.y - boundingHeight/2}
          width={boundingWidth}
          height={boundingHeight}
          fill="transparent"
          stroke="#00ff00"
          strokeWidth={2}
          dash={[5, 5]}
          listening={false}
        />
        
        {/* SVG Arrow */}
        <Image
          ref={imageRef}
          image={arrowImage}
          x={arrowProps.x}
          y={arrowProps.y}
          width={arrowProps.width}
          height={arrowProps.height}
          offsetX={arrowProps.width / 2}
          offsetY={arrowProps.height / 2}
          rotation={arrowProps.rotation}
          draggable={true}
          onTransformEnd={handleArrowTransform}
          onDragEnd={handleArrowTransform}
        />
      </Group>
    );
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f0f0f0'
    }}>
      <div 
        ref={containerRef}
        style={{
          border: '2px solid #333',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          width: `${displayWidth}px`,
          height: `${displayHeight}px`
        }}
      >
        <Stage
          ref={stageRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          scaleX={scale}
          scaleY={scale}
          style={{ backgroundColor: 'black' }}
        >
          <Layer>
            {/* Video Background */}
            {videoElement && isVideoReady && (
              <Image
                ref={imageRef}
                image={videoElement}
                x={videoProps.x}
                y={videoProps.y}
                width={videoProps.width}
                height={videoProps.height}
              />
            )}
            
            {/* Test Arrow with Bounding Box */}
            <TestArrow />
          </Layer>
        </Stage>
      </div>
    </div>
  );
}

export default VideoCanvasPage;